import Button from '@mui/material/Button'
import { alpha, useTheme } from '@mui/material/styles'
// eslint-disable-next-line @nx/enforce-module-boundaries
import Iconify from '@repo/shared-core-ui-components/Iconify/iconify'
import { uploadImage } from '@repo/shared-images-client-utils'
import {
  isTouchDevice,
  MenuButtonAddTable,
  MenuButtonBlockquote,
  MenuButtonBold,
  MenuButton,
  MenuButtonBulletedList,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonEditLink,
  MenuButtonHighlightColor,
  MenuButtonHorizontalRule,
  MenuButtonImageUpload,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonSubscript,
  MenuButtonSuperscript,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectFontSize,
  MenuSelectHeading,
  MenuSelectTextAlign,
  useRichTextEditorContext
} from 'mui-tiptap'
import Divider from '@mui/material/Divider'
import { editorClasses } from './classes'
import Stack from '@mui/material/Stack'
import YouTubeIcon from '@mui/icons-material/YouTube'

export function EditorMenuControls({ simple = false }: { simple?: boolean }) {
  const theme = useTheme()
  const editor = useRichTextEditorContext()

  return (
    <Stack
      spacing={1}
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      divider={<Divider orientation="vertical" flexItem sx={{ height: 16, my: 'auto' }} />}
      className={editorClasses.toolbar.root}
      sx={{
        p: 1.25,
        bgcolor: 'background.paper',
        borderTopRightRadius: 'inherit',
        borderTopLeftRadius: 'inherit',
        borderBottom: theme => `solid 1px ${alpha(theme.palette.grey['500'], 0.2)}`
      }}
    >
      <MenuControlsContainer>
        {/*
        <MenuSelectFontFamily
          options={[
            { label: 'Comic Sans', value: 'Comic Sans MS, Comic Sans' },
            { label: 'Cursive', value: 'cursive' },
            { label: 'Monospace', value: 'monospace' },
            { label: 'Serif', value: 'serif' }
          ]}
        />

        <MenuDivider />
        */}

        <MenuSelectHeading />

        <MenuDivider />

        <MenuSelectFontSize />

        <MenuDivider />

        <MenuButtonBold />
        <MenuButtonItalic />
        <MenuButtonUnderline />
        <MenuButtonStrikethrough />
        {simple !== true && (
          <>
            <MenuButtonSubscript />
            <MenuButtonSuperscript />
          </>
        )}

        <MenuDivider />

        {simple !== true && (
          <>
            <MenuButtonTextColor
              defaultTextColor={theme.palette.text.primary}
              swatchColors={[
                { value: '#000000', label: 'Black' },
                { value: '#ffffff', label: 'White' },
                { value: '#888888', label: 'Grey' },
                { value: '#ff0000', label: 'Red' },
                { value: '#ff9900', label: 'Orange' },
                { value: '#ffff00', label: 'Yellow' },
                { value: '#00d000', label: 'Green' },
                { value: '#0000ff', label: 'Blue' }
              ]}
            />
            <MenuButtonHighlightColor
              swatchColors={[
                { value: '#595959', label: 'Dark grey' },
                { value: '#dddddd', label: 'Light grey' },
                { value: '#ffa6a6', label: 'Light red' },
                { value: '#ffd699', label: 'Light orange' },
                // Plain yellow matches the browser default `mark` like when using Cmd+Shift+H
                { value: '#ffff00', label: 'Yellow' },
                { value: '#99cc99', label: 'Light green' },
                { value: '#90c6ff', label: 'Light blue' },
                { value: '#8085e9', label: 'Light purple' }
              ]}
            />

            <MenuDivider />
          </>
        )}

        <MenuButtonEditLink />

        <MenuDivider />

        <MenuSelectTextAlign />

        <MenuDivider />

        <MenuButtonOrderedList />
        <MenuButtonBulletedList />

        {/* On touch devices, we'll show indent/unindent buttons, since they're
      unlikely to have a keyboard that will allow for using Tab/Shift+Tab. These
      buttons probably aren't necessary for keyboard users and would add extra
      clutter. */}
        {isTouchDevice() && (
          <>
            <MenuButtonIndent />
            <MenuButtonUnindent />
          </>
        )}

        <MenuDivider />

        {simple !== true && (
          <>
            <MenuButtonBlockquote />

            <MenuDivider />
          </>
        )}

        {simple !== true && (
          <>
            <MenuButtonCode />
            <MenuButtonCodeBlock />

            <MenuDivider />
          </>
        )}

        <MenuButtonImageUpload
          onUploadFiles={async files => {
            const uploadedFiles = []

            for (const file of files) {
              const uploadedImage = await uploadImage({ file })

              uploadedFiles.push({
                src: uploadedImage?.url,
                alt: file.name
              })
            }

            return uploadedFiles
          }}
        />

        <MenuDivider />

        {simple !== true && (
          <>
            <MenuButtonHorizontalRule />
            <MenuButtonAddTable />

            <MenuDivider />
          </>
        )}

        <MenuButtonRemoveFormatting />

        {simple !== true && (
          <>
            <MenuDivider />

            <MenuButtonUndo />
            <MenuButtonRedo />

            <MenuDivider />
          </>
        )}

        {simple !== true && (
          <>
            <MenuButton
              tooltipLabel="Add Youtube Video"
              // tooltipShortcutKeys={["mod", "B"]}
              IconComponent={YouTubeIcon}
              selected={editor?.isActive('youtube') ?? false}
              disabled={!editor?.isEditable}
              onClick={() => {
                const url = prompt('Enter YouTube URL')

                if (url) {
                  editor?.commands.setYoutubeVideo({
                    src: url,
                    width: 640,
                    height: 480
                  })
                }
              }}
            />
            <MenuButton
              tooltipLabel="Add iFrame"
              // tooltipShortcutKeys={["mod", "B"]}
              // IconComponent={<Iconify icon={'mdi:iframe-brackets'} />}
              children={<Iconify icon={'mdi:iframe-brackets'} />}
              selected={editor?.isActive('iframe') ?? false}
              disabled={!editor?.isEditable}
              onClick={() => {
                const url = window.prompt('URL')

                if (url) {
                  editor.chain().focus().setIframe({ src: url }).run()
                }
              }}
            />
            <Button
              disabled={!editor?.isEditable || !editor?.isActive('iframe')}
              onClick={() => {
                const width = window.prompt('Width (1% - 100%)')

                editor.chain().focus().setIframeWidth({ width: width }).run()
              }}
            >
              iFrame Width
            </Button>
          </>
        )}
      </MenuControlsContainer>
    </Stack>
  )
}
